import LayoutCenter from "@/components/LayoutCenter";
import { Alert } from "@/components/ui/alert";
import { Button, buttonVariants } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { PageProps } from "@/types/PageProps";
import { Head, Link, router } from "@inertiajs/react";
import { AlertCircle, ArrowRightIcon, CheckIcon } from "lucide-react";
import posthog from "posthog-js";
import { SubmitHandler, useForm } from "react-hook-form";
import Logo from "../assets/fulllogo_transparent.png";

type Inputs = {
  email: string;
  password: string;
};

type Props = PageProps & {
  error: string;
  notice: string;
  just_logged_out: boolean;
};

export default function Session({ error, notice, user, just_logged_out }: Props) {
  const { register, handleSubmit } = useForm<Inputs>();

  const onSubmit: SubmitHandler<Inputs> = (data) => {
    router.post("/session", data);
  };

  if (just_logged_out) {
    posthog.reset();
  }

  if (user) {
    return (
      <LayoutCenter>
        <Head title="Login" />
        <p className="mb-4">You are already logged in.</p>
        <Link href="/" className={buttonVariants({ size: "lg" })}>
          Go to your dashboard <ArrowRightIcon className="w-4 h-4" />
        </Link>
      </LayoutCenter>
    );
  }

  return (
    <LayoutCenter>
      <Head title="Login" />
      {error && (
        <Alert variant="destructive" className="mb-8 max-w-sm">
          <div className="flex items-center gap-2">
            <AlertCircle className="h-4 w-4" />
            <p className="text-sm text-destructive">{error}</p>
          </div>
        </Alert>
      )}
      {notice && (
        <Alert className="mb-8 max-w-sm">
          <div className="flex items-center gap-2">
            <CheckIcon className="h-4 w-4" />
            <p className="text-sm">{notice}</p>
          </div>
        </Alert>
      )}

      <img src={Logo} className="h-12 mb-8" />

      <Card className="mx-auto max-w-sm w-full">
        <CardHeader>
          <CardTitle className="text-2xl">Login</CardTitle>
        </CardHeader>
        <CardContent>
          <form action="/session" method="post" onSubmit={handleSubmit(onSubmit)}>
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input id="email" type="email" required {...register("email")} />
              </div>
              <div className="space-y-2">
                <div className="flex items-center">
                  <Label htmlFor="password">Password</Label>
                </div>
                <Input id="password" type="password" required {...register("password")} />
              </div>
              <Button type="submit" className="w-full">
                Login
              </Button>
            </div>
            <div className="mt-6 text-center text-sm">
              Don't have an account?{" "}
              <Link href="/register" className="underline">
                Sign up
              </Link>
            </div>
            <div className="mt-4 text-center text-sm">
              <a href="/passwords/new" className="ml-auto inline-block text-sm underline">
                Forgot your password?
              </a>
            </div>
          </form>
        </CardContent>
      </Card>
    </LayoutCenter>
  );
}
